import {
	ChangeEvent,
	FunctionComponent,
	useCallback,
	useEffect,
	useMemo,
	useRef,
	useState,
} from "react";
import { useTranslation } from "react-i18next";
import { Stack, TextField, Typography } from "@mui/material";

import {
	faTrashCan,
	faPen,
	faPlus,
	faCreditCard,
	faCheck,
	faTimes,
	faIdBadge,
} from "@fortawesome/pro-regular-svg-icons";

import { format } from "date-fns";

import { Link } from "react-router-dom";

import {
	useDeleteCompaniesMutation,
	useLazyGetCompaniesQuery,
} from "@/pages/Private/redux/companies/companies.api";

import { Table, TableColumn } from "@/components/Table/Table";

import { Icon, LoadingOverlay, Modal, PermissionCheck } from "@/components";
import { useTableSearch } from "@/utils";
import { ensureHttpProtocol } from "@/utils/url";
import { useSnackBar } from "@/utils/useSnackbar";

import { PermissionRoles } from "@/enum";

import { InputField } from "@/components/InputField/InputField";

import { LinkedinIcon } from "@/components/LinkedinIcon/LinkedinIcon";

import { WebsiteIcon } from "@/components/WebsiteIcon/WebsiteIcon";

import { useAppDispatch, useAppSelector } from "@/redux/hooks";

import {
	companiesSelector,
	setCompaniesSorting,
} from "@/pages/Private/redux/companies/companies.slice";

import { SortParams } from "@/types/types";

import { Companies } from "../schema/companies";
import { UpdateCompaniesModal } from "./UpdateCompaniesModal";
import { TopupCompaniesModal } from "./TopupCompaniesModal";
import { ManageLicencesModal } from "./ManageLicencesModal";
import { DemoEndsCell } from "./DemoEndsCell";
import { LicencesCell } from "./LicencesCell";
import { MatchedUsersCell } from "./MatchedUsersCell";
import { LifecycleDatesCell, LifecycleStateCell } from "./LifecycleCell";
import { RowActionIcon } from "./RowActionIcon";
import { getOrderStatus } from "../../List/utils/column-utils";

export const CompaniesTable: FunctionComponent = () => {
	const { t } = useTranslation();
	const { sortingCompanies, companies: data, meta } = useAppSelector(companiesSelector);

	const ts = useCallback((key: string) => t(`companies.${key}`), [t]);
	const [showEditModal, setShowEditModal] = useState<boolean>(false);
	const [showDeleteModal, setShowDeleteModal] = useState<boolean>(false);
	const [showTopupModal, setShowTopupModal] = useState<boolean>(false);
	const [showLicencesModal, setShowLicencesModal] = useState<boolean>(false);
	const [selectedCompanies, setSelectedCompanies] = useState<Companies | null>(null);
	const { handleOpenSnackbar, renderMessageSnackbar } = useSnackBar();
	const { page, setPage, limit, setLimit, searchValue, setSearchValue, query } = useTableSearch();
	const [deleteCompanies, { isLoading: isDeleteLoading }] = useDeleteCompaniesMutation();
	const dispatch = useAppDispatch();

	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	const triggerRef = useRef<any>();

	const [getCompanies, { isLoading, isFetching }] = useLazyGetCompaniesQuery();

	useEffect(() => {
		if (triggerRef.current) {
			triggerRef.current.abort();
		}

		triggerRef.current = getCompanies({
			page,
			limit,
			searchValue: query,
			sorting: sortingCompanies,
		});
	}, [page, limit, query, sortingCompanies]);

	const handleSorting = (sortParams: SortParams) => {
		setPage(1);
		dispatch(setCompaniesSorting(sortParams));
	};

	const columns: TableColumn[] = useMemo(
		() => [
			{
				title: ts("name"),
				uid: "name",
				orderTitle: "name",
				orderStatus: getOrderStatus(sortingCompanies, "name"),
				render: (row: Companies) => (
					<Stack alignItems={"flex-start"} direction="row" justifyContent="flex-start">
						<Stack alignItems="flex-start" direction="column" justifyContent="flex-start">
							<Link to={`/app/companies/${row?.id}`}>
								<Typography color="inherit" mb={0} variant="body2">
									{row?.name}
								</Typography>
							</Link>
						</Stack>
					</Stack>
				),
			},
			{
				title: "Onboarded",
				uid: "onboarded",
				orderTitle: "isOnboarded",
				orderStatus: getOrderStatus(sortingCompanies, "isOnboarded"),
				render: (row: Companies) => (
					<Stack alignItems={"flex-start"} direction="row" justifyContent="flex-start">
						{row.isOnboarded ? (
							<Icon className="text-status-success" icon={faCheck} />
						) : (
							<Icon className="text-status-error" icon={faTimes} />
						)}
					</Stack>
				),
			},
			{
				title: "Urls",
				uid: "urls",
				orderTitle: "website",
				orderStatus: getOrderStatus(sortingCompanies, "website"),
				render: (row: Companies) => (
					<Stack alignItems={"flex-start"} direction="row" justifyContent="flex-start">
						<Stack alignItems="flex-start" direction="row" justifyContent="flex-start">
							<Link
								className="flex flex-row gap-2"
								target="_blank"
								to={ensureHttpProtocol(row?.linkedinUrl)}
							>
								<LinkedinIcon status="checked" />
							</Link>
							{row?.website ? (
								<Link
									className="flex flex-col gap-2"
									target="_blank"
									to={ensureHttpProtocol(row?.website)}
								>
									<WebsiteIcon status="checked" />
								</Link>
							) : (
								<WebsiteIcon status="failed" />
							)}
						</Stack>
					</Stack>
				),
			},
			{
				title: ts("users"),
				uid: "users",
				render: (row: Companies) => (
					<MatchedUsersCell
						companyId={row?.id}
						matchedUsers={row?.matchedUsers}
						userCount={row?.userCount}
					/>
				),
			},
			{
				title: ts("licences.column"),
				uid: "licences",
				render: (row: Companies) => <LicencesCell seats={row?.seats} />,
			},
			{
				title: "Subscriptions",
				uid: "subscriptions",
				render: (row: Companies) => (
					<Stack alignItems={"flex-start"} direction="row" justifyContent="flex-start">
						<Stack alignItems="flex-start" direction="column" justifyContent="flex-start">
							<Typography color="inherit" mb={0} variant="body2">
								{row?.subscriptions?.[0]?.stripeSubscriptionId && <Icon icon={faCreditCard} />}
								{row?.subscriptions?.[0] && (
									<>
										{row?.subscriptions?.[0]?.product?.name} -{" "}
										{row?.subscriptions?.[0]?.price?.price}
										€/
										{row?.subscriptions?.[0]?.price?.perMonthOrYear?.toLowerCase()}
									</>
								)}
							</Typography>
						</Stack>
					</Stack>
				),
			},
			{
				title: "Next subscription",
				uid: "nextSubscription",
				render: (row: Companies) => (
					<Stack alignItems={"flex-start"} direction="row" justifyContent="flex-start">
						<Stack alignItems="flex-start" direction="column" justifyContent="flex-start">
							<Typography color="inherit" mb={0} variant="body2">
								{row?.subscriptions?.[1]?.stripeSubscriptionId && <Icon icon={faCreditCard} />}
								{row?.subscriptions?.[1] && (
									<>
										{row?.subscriptions?.[1]?.product?.name} -{" "}
										{row?.subscriptions?.[1]?.price?.price}
										€/
										{row?.subscriptions?.[1]?.price?.perMonthOrYear?.toLowerCase()}
									</>
								)}
							</Typography>
						</Stack>
					</Stack>
				),
			},
			{
				title: "Credits",
				uid: "credits",
				render: (row: Companies) => (
					<Stack alignItems={"flex-start"} direction="row" justifyContent="flex-start">
						<Stack alignItems="flex-start" direction="column" justifyContent="flex-start">
							<Typography color="inherit" mb={0} variant="body2">
								{row?.credits ?? 0}
							</Typography>
						</Stack>
					</Stack>
				),
			},
			{
				title: "Website scrape credits",
				uid: "websiteScrapeCredits",
				render: (row: Companies) => (
					<Stack alignItems={"flex-start"} direction="row" justifyContent="flex-start">
						<Stack alignItems="flex-start" direction="column" justifyContent="flex-start">
							<Typography color="inherit" mb={0} variant="body2">
								{row?.websiteScrapeCredits ?? 0}
							</Typography>
						</Stack>
					</Stack>
				),
			},
			{
				title: "Demo ends in",
				uid: "demoEndsIn",
				orderTitle: "demoEndsAt",
				orderStatus: getOrderStatus(sortingCompanies, "demoEndsAt"),
				render: (row: Companies) => <DemoEndsCell row={row} />,
			},
			{
				title: ts("lifecycle.stateColumn"),
				uid: "lifecycleState",
				orderTitle: "cancellationEffectiveAt",
				orderStatus: getOrderStatus(sortingCompanies, "cancellationEffectiveAt"),
				render: (row: Companies) => <LifecycleStateCell state={row?.lifecycleState} />,
			},
			{
				title: ts("lifecycle.datesColumn"),
				uid: "lifecycleDates",
				orderTitle: "archivedAt",
				orderStatus: getOrderStatus(sortingCompanies, "archivedAt"),
				render: (row: Companies) => (
					<LifecycleDatesCell
						archivedAt={row?.archivedAt}
						deletionAt={row?.deletionAt}
						readOnlyUntil={row?.readOnlyUntil}
						state={row?.lifecycleState}
					/>
				),
			},
			{
				title: "Created At",
				uid: "createdAt",
				orderTitle: "createdAt",
				orderStatus: getOrderStatus(sortingCompanies, "createdAt"),
				render: (row: Companies) => (
					<Stack alignItems={"flex-start"} direction="row" justifyContent="flex-start">
						<Stack alignItems="flex-start" direction="column" justifyContent="flex-start">
							<Typography color="inherit" mb={0} variant="body2">
								{row?.createdAt ? format(new Date(row?.createdAt), "dd.MM.yyyy") : ""}
							</Typography>
						</Stack>
					</Stack>
				),
			},
			{
				title: "Created By",
				uid: "createdBy",
				render: (row: Companies) => (
					<Stack alignItems={"flex-start"} direction="row" justifyContent="flex-start">
						<Stack alignItems="flex-start" direction="column" justifyContent="flex-start">
							<Typography color="inherit" mb={0} variant="body2">
								{row?.createdBy?.firstName} {row?.createdBy?.lastName}
							</Typography>
						</Stack>
					</Stack>
				),
			},
			{
				title: "",
				uid: "actions",
				render: (row: Companies) => (
					<Stack direction="row" justifyContent="flex-end" spacing={2}>
						<PermissionCheck requiredPermissions={[PermissionRoles.COOKIE]}>
							<>
								<RowActionIcon
									icon={faPlus}
									label="Topup credits"
									testId="company-topup"
									onClick={() => handleTopupModal(row)}
								/>
								<RowActionIcon
									icon={faIdBadge}
									label={ts("licences.manage")}
									testId="company-licences"
									onClick={() => handleLicencesModal(row)}
								/>
								<RowActionIcon
									icon={faPen}
									label={ts("update.title")}
									testId="company-edit"
									onClick={() => handleEditCompanies(row)}
								/>
								<RowActionIcon
									icon={faTrashCan}
									label={ts("delete.title")}
									testId="company-delete"
									onClick={() => handleDeleteModal(row)}
								/>
							</>
						</PermissionCheck>
					</Stack>
				),
			},
		],
		[ts, sortingCompanies, data]
	);

	const handleChangeRowsPerPage = (newLimit: number) => {
		setLimit(newLimit);
		setPage(1);
	};

	const handleEditCompanies = (row: Companies) => {
		setShowEditModal(true);
		setSelectedCompanies(row);
	};

	const handleDeleteModal = (row: Companies) => {
		setShowDeleteModal(true);
		setSelectedCompanies(row);
	};

	const handleTopupModal = (row: Companies) => {
		setShowTopupModal(true);
		setSelectedCompanies(row);
	};

	const handleLicencesModal = (row: Companies) => {
		setShowLicencesModal(true);
		setSelectedCompanies(row);
	};

	const handleDeleteCompanies = async () => {
		try {
			await deleteCompanies(selectedCompanies?.id ?? 0).unwrap();
			setShowDeleteModal(false);
		} catch (err) {
			console.error(err);
		}
	};

	return !data && !isLoading ? (
		<LoadingOverlay />
	) : (
		<div className="w-full">
			<div className="flex items-end align-baseline justify-between w-full mb-8">
				<div className="flex-grow mr-4 ">
					<InputField
						handleChange={(e: ChangeEvent<HTMLInputElement>) => {
							setSearchValue(e.target.value);
						}}
						label={ts("search")}
						name={"search"}
						placeholder="Search..."
						value={searchValue}
					/>
				</div>
			</div>
			<div className="w-full bg-white">
				<Table
					columns={columns}
					data={data || []}
					handleChangeRowsPerPage={handleChangeRowsPerPage}
					handleFetchPage={(page) => {
						setPage(page);
					}}
					handleSorting={(sortParams: SortParams) => {
						setPage(1);
						handleSorting(sortParams);
					}}
					hidePagination={false}
					isFetching={isFetching}
					isLoading={isLoading}
					limit={limit}
					overallCount={meta?.itemCount}
					page={page}
					totalPages={meta?.pageCount}
				/>
			</div>
			{showDeleteModal && (
				<Modal
					handleClose={() => setShowDeleteModal(false)}
					handleSave={handleDeleteCompanies}
					isLoading={isDeleteLoading}
					isOpened={showDeleteModal}
					submitButtonColor="error"
					submitButtonText={t("basics.delete")}
					title={ts("delete.title")}
				>
					<TextField
						fullWidth
						defaultValue={selectedCompanies?.name}
						inputProps={{ readOnly: true }}
						label={ts("name")}
					/>
				</Modal>
			)}
			{showEditModal && selectedCompanies && (
				<UpdateCompaniesModal
					companies={selectedCompanies}
					isOpen={showEditModal}
					setIsOpen={setShowEditModal}
				/>
			)}
			{showTopupModal && selectedCompanies && (
				<TopupCompaniesModal
					companies={selectedCompanies}
					isOpen={showTopupModal}
					setIsOpen={setShowTopupModal}
				/>
			)}
			{showLicencesModal && selectedCompanies && (
				<ManageLicencesModal
					companyId={selectedCompanies.id}
					companyName={selectedCompanies.name}
					isOpen={showLicencesModal}
					setIsOpen={setShowLicencesModal}
					onSaved={() => handleOpenSnackbar(ts("licences.saved"))}
				/>
			)}
			{renderMessageSnackbar()}
		</div>
	);
};
